/**
 * Server-side compare exports: estimate, jobs, telemetry
 * (backend/app/api/export_job_routes.py).
 */
import { authApiClient } from './authApi';
import type { ServerExportFormat } from '../utils/exportPayload';
import type { ColoredTerm } from '../utils/highlightColors';
import type { GatherHit } from '../utils/exportGather';
import type { ConceptEntry } from '../utils/universalSections';

export interface EstimateItem {
  drug_id: number;
  source?: string;
  document_id?: number;
  letter_section_id?: number;
}

export interface ExportEstimate {
  estimated_seconds: number;
  recommended_path: 'client' | 'server';
  browser_fallback_allowed: boolean;
  server_available: boolean;
  drugs: number;
  pdf_pages: number;
  spl_chars: number;
  pages_to_embed: number;
  estimated_mb: number;
  images_omitted: boolean;
  /** Files the server splits the export into (1 = one file; more = one ZIP). */
  image_parts?: number;
  client_max_seconds: number;
  browser_fallback_max_seconds: number;
  cost_model_version: string;
}

export type ExportJobStatus = 'awaiting_upload' | 'pending' | 'running' | 'complete' | 'error' | 'cancelled' | 'expired';

export interface ExportJob {
  job_id: string;
  status: ExportJobStatus;
  format: ServerExportFormat;
  progress_pct: number;
  heartbeat_at: string | null;
  error_message: string | null;
  image_note: string | null;
  result_filename: string | null;
  result_bytes: number | null;
  estimated_seconds: number | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string;
  request: CreateExportJobRequest & { return_url?: string };
}

export interface CreatedExportJob extends ExportJob {
  upload_url: string | null;
  upload_content_type: string | null;
  deduplicated: boolean;
  /** The server gathers the drugs itself: nothing to gather, upload or submit.
   *  Absent from a backend without server gather. */
  server_gather?: boolean;
}

/** What the page knows that the backend doesn't, so the export worker can
 *  gather the drugs itself (backend GatherSpecIn, frontend GatherContext). */
export interface ExportGatherSpec {
  hits: GatherHit[];
  highlight_terms: ColoredTerm[];
  is_cogni: boolean;
  evidence: Record<string, string>;
  export_v2: boolean;
  /** Display label only (US vs US-DailyMed in the file). */
  us_fda_visible?: boolean;
}

export interface CreateExportJobRequest {
  format: ServerExportFormat;
  drug_ids: number[];
  keyword: string;
  section?: string;
  colored_terms?: ColoredTerm[];
  col_sections?: Record<string, string[]>;
  col_concepts?: Record<string, ConceptEntry[]>;
  manual_highlights?: Record<string, string[]>;
  include_page_text?: boolean;
  estimated_seconds?: number;
  return_url?: string;
  /** Always sent; a backend with server gather off ignores it and returns an
   *  upload_url, and the browser gathers + uploads as before. */
  gather?: ExportGatherSpec;
}

export interface ExportTelemetry {
  path: 'client' | 'server';
  format: ServerExportFormat | 'xls';
  outcome: 'complete' | 'cancelled' | 'error';
  drugs: number;
  pdf_pages?: number;
  spl_chars?: number;
  media_bytes?: number;
  estimated_seconds?: number;
  total_ms: number;
  stage_ms: Record<string, number>;
  cores?: number;
  cost_model_version?: string;
  job_id?: string;
}

export const ACTIVE_EXPORT_STATUSES: readonly ExportJobStatus[] = ['awaiting_upload', 'pending', 'running'];

export const exportJobsApi = {
  async estimate(format: ServerExportFormat, items: EstimateItem[]): Promise<ExportEstimate> {
    return (await authApiClient.post('/export/compare-estimate', { format, items })).data;
  },
  async create(body: CreateExportJobRequest): Promise<CreatedExportJob> {
    return (await authApiClient.post('/export/compare-jobs', body)).data;
  },
  async submit(jobId: string): Promise<ExportJob> {
    return (await authApiClient.post(`/export/compare-jobs/${jobId}/submit`)).data;
  },
  async get(jobId: string): Promise<ExportJob> {
    return (await authApiClient.get(`/export/compare-jobs/${jobId}`)).data;
  },
  async cancel(jobId: string): Promise<ExportJob> {
    return (await authApiClient.post(`/export/compare-jobs/${jobId}/cancel`)).data;
  },
  async downloadUrl(jobId: string): Promise<{ url: string; filename: string }> {
    return (await authApiClient.get(`/export/compare-jobs/${jobId}/download`)).data;
  },
  async previewUrl(jobId: string): Promise<{ url: string }> {
    return (await authApiClient.get(`/export/compare-jobs/${jobId}/preview`)).data;
  },
  /** Fire-and-forget: telemetry must never break an export. */
  telemetry(body: ExportTelemetry): void {
    authApiClient.post('/export/telemetry', body).catch(() => {});
  },
};
